/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, useId, useState, useMemo } from "react";
import useColumnDefinitions from "./Table/colHeaderConst";
import useCustomCellRenderers from "./Table/customCellConst";
import CustomPagination from "../../components/CustomPagination";
import {
  MotifErrorMessage,
  MotifFormField,
  MotifTable,
} from "@ey-xd/motif-react";
import SearchInput from "../../components/SearchInput/SearchInput";
import "./Table/table.scss";
import {
  customPaginationPageSize,
  customPaginationPageSizeSelector,
  EMOJI_ERROR_MESSAGE,
} from "../../Constant";
import { fetchSkuListData } from "../../store/slices/CTDBSkuListSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { mapSkuListAPIDataWithFormFields } from "./Helper";
import UpdateSkuModal from "../CreateSku/UpdateSkuModal";
import PreviewSkuModal from "../PreviewSkuModal";
import { SKUToast } from "../../components/SKUToast/SKUToast";
import DeleteSkuModal from "./Components/DeleteSkuModal";
import GenerateKeyModal from "../CreateSku/GenerateKeyModal/GenerateKeyModal";
import {
  generateKey,
  fetchSkuApiKey,
} from "../../store/slices/CTDBSkuConfigSlice";
import { containsEmoji } from "../../utils/common";

type SkuListProps = {
  productId: string;
  reloadOnNewCreation: boolean;
  setReloadOnNewCreation: React.Dispatch<React.SetStateAction<boolean>>;
  isCommercialOrProxyOwner: boolean;
  setIsAnySKUretired: React.Dispatch<React.SetStateAction<boolean>>;
  hasAnyProductError?: boolean;
  isConsumptionManager: boolean;
  defaultSearchSkuId?: string;
};

export default function SkuList({
  productId,
  reloadOnNewCreation,
  setReloadOnNewCreation,
  isCommercialOrProxyOwner,
  setIsAnySKUretired,
  hasAnyProductError,
  isConsumptionManager = false,
  defaultSearchSkuId,
}: SkuListProps) {
  const paginationPageSize: number = customPaginationPageSize;
  const paginationPageSizeSelector: number[] = customPaginationPageSizeSelector;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [paginationObj, setPaginationObj] = useState<any>({
    currentPage: 1,
    pageSize: paginationPageSize,
    searchText: defaultSearchSkuId || "",
    SortOrder: "",
    SortColumn: "",
  });
  const [showUpdateSkus, setShowUpdateSkus] = useState(false);
  const [showDeleteSkuModal, setShowDeleteSkuModal] = useState(false);
  const [showDeleteSkuSuccess, setShowDeleteSkuSuccess] = useState(false);
  const [showDeleteSkuError, setShowDeleteSkuError] = useState(false);

  const [showPreviewSkus, setShowPreviewSkus] = useState(false);

  const [selectedSku, setSelectedSku] = useState("");
  const [showSuccessPage, setShowSuccessPage] = useState(false);
  const [showGenerateKeyModal, setShowGenerateKeyModal] = useState(false);
  const [showGenerateKeySuccessToast, setShowGenerateKeySuccessToast] =
    useState(false);
  const [showFullKey, setShowFullKey] = useState(false);
  const [showViewKeysModal, setShowViewKeysModal] = useState(false);
  const [selectedSkuName, setSelectedSkuName] = useState("");
  const [key, setKey] = useState("");
  const [searchValidationError, setSearchValidationError] =
    useState<string>("");

  const searchSkuListID = useId();
  const searchSkuListID_EmptyTable = useId();

  const handleCloseGenerateKeyModal = () => {
    setShowGenerateKeyModal(false);
    setShowFullKey(false);
    setShowGenerateKeySuccessToast(false);
    setKey("");
    setShowViewKeysModal(false);
    setSelectedSku("");
    setReloadOnNewCreation(true);
  };

  const handleKey = (sku: string, isAvailable?: boolean) => {
    setSelectedSku(sku);
    setKey("");
    setShowGenerateKeySuccessToast(false);
    setShowFullKey(Boolean(isAvailable));
    setShowViewKeysModal(Boolean(isAvailable));
    setShowGenerateKeyModal(true);

    if (isAvailable) {
      const payload: any = {
        Action: "fetchSkuApiKey",
        SkuId: sku,
      };

      dispatch(fetchSkuApiKey(payload)).then((action: any) => {
        if (action.type.endsWith("fulfilled")) {
          setKey(action.payload?.Data?.SkuApiKey);
        } else if (action.type.endsWith("rejected")) {
          setShowGenerateKeyModal(false);
          console.log(
            "Error fetching key:",
            action.payload?.message || "Error fetching key",
          );
        }
      });
    } else {
      const payload: any = {
        Action: "generateSkuApiKey",
        SkuId: sku,
      };

      dispatch(generateKey(payload)).then((action: any) => {
        if (action.type.endsWith("fulfilled")) {
          setKey(action.payload?.Data?.SkuKey);
          setIsAnySKUretired(true);
        } else if (action.type.endsWith("rejected")) {
          setShowGenerateKeyModal(false);
          console.log(
            "Error generating key:",
            action.payload?.message || "Error generating key",
          );
        }
      });
    }
  };

  const cellRenderers = useCustomCellRenderers(
    setShowUpdateSkus,
    setShowDeleteSkuModal,
    setSelectedSku,
    setShowPreviewSkus,
    setShowGenerateKeyModal,
    setSelectedSkuName,
    handleKey,
    isConsumptionManager,
  );

  const columnDefs = useColumnDefinitions({ paginationObj, setPaginationObj });

  const isLoading = useSelector(
    (state: RootState) => state.CTDBSkuListSlice.isLoading,
  );
  const apiData = useSelector(
    (state: RootState) => state.CTDBSkuListSlice.data,
  );
  const error = useSelector((state: RootState) => state.CTDBSkuListSlice.error);

  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    if (showSuccessPage === true) {
      setTimeout(function () {
        setShowSuccessPage(false);
      }, 5000);
    }
    // Scroll to top on component mount
  }, [showSuccessPage]);

  useEffect(() => {
    if (showDeleteSkuSuccess === true) {
      setTimeout(function () {
        setShowDeleteSkuSuccess(false);
      }, 5000);
    }
    // Scroll to top on component mount
  }, [showDeleteSkuSuccess]);

  useEffect(() => {
    if (showDeleteSkuError === true) {
      setTimeout(function () {
        setShowDeleteSkuError(false);
      }, 5000);
    }
    // Scroll to top on component mount
  }, [showDeleteSkuError]);

  useEffect(() => {
    if (showDeleteSkuModal === true) {
      setShowDeleteSkuError(false);
      setShowDeleteSkuSuccess(false);
    }
  }, [showDeleteSkuModal]);

  useEffect(() => {
    if (reloadOnNewCreation) {
      setReloadOnNewCreation(false);
    }
    dispatch(
      fetchSkuListData({
        search: paginationObj.searchText,
        productId: productId, // Replace with actual product ID
        page: paginationObj.currentPage,
        record: paginationObj.pageSize,
        sortOrder: paginationObj.SortOrder,
        sortColumn: paginationObj.SortColumn,
      }),
    );
  }, [paginationObj, productId, reloadOnNewCreation]);

  const handleSearch = (searchText: string) => {
    setPaginationObj({
      ...paginationObj,
      currentPage: 1,
      pageSize: Number(paginationObj.pageSize),
      searchText: searchText,
    });
  };

  const rowData = useMemo(() => {
    if (!apiData || !apiData.Result?.length) return [];
    const skuWithStatus = (apiData.Result as any[]).map((s: any) => ({
      ...s,
      StatusName: s.StatusName ?? "",
    }));
    const mapped = mapSkuListAPIDataWithFormFields(skuWithStatus) || [];
    return (mapped as any[]).map((row: any) => ({
      ...row,
      showDeleteBtn: !isConsumptionManager && isCommercialOrProxyOwner,
    }));
  }, [apiData, isCommercialOrProxyOwner]);
  const totalRecords =
    apiData && typeof apiData.TotalRecord === "number"
      ? apiData.TotalRecord
      : 0;

  let overlayNoRowsTemplate = "";
  if (isLoading) {
    overlayNoRowsTemplate = "Loading...";
  } else if (error) {
    overlayNoRowsTemplate = "Error fetching data";
  } else if (paginationObj.searchText && totalRecords === 0) {
    overlayNoRowsTemplate = "No Record found";
  } else if (!paginationObj.searchText && totalRecords === 0) {
    overlayNoRowsTemplate = "No data found";
  }

  //@ts-ignore
  const totalPages =
    apiData?.Result?.length && apiData?.Result?.length > 0
      ? Math.ceil(totalRecords / paginationObj.pageSize)
      : 0;

  // Only show loading table when loading
  if (isLoading) {
    return (
      <div className="sku-list-table-container">
        <MotifTable
          key={overlayNoRowsTemplate}
          className="LoadingTable"
          // onGridReady={onGridReady}
          overlayNoRowsTemplate={overlayNoRowsTemplate}
          columnTypes={{}}
          //@ts-expect-error
          columnDefs={columnDefs}
          components={cellRenderers}
          description={`Manage SKUs, update costs or descriptions, and create API keys to register external orders and variable charges.`}
          //@ts-ignore
          rowData={[]}
          title={"SKU Listing"}
          suppressMovableColumns
          titleComplement={
            <MotifFormField>
              <SearchInput
                id={`skuList-${searchSkuListID_EmptyTable}`}
                className="skuListSearch-emptyTable"
                aria-label={"Search"}
                onChange={() => {}}
                clearSearch={() => {
                  setPaginationObj({
                    ...paginationObj,
                    searchText: "",
                    currentPage: 1,
                    pageSize: paginationPageSize,
                  });
                }}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onEnter={(e: any) => handleSearch(e.target.value)}
                placeholder={"Search"}
                value={paginationObj.searchText}
              />
            </MotifFormField>
          }
        />
      </div>
    );
  }

  // Show empty table if no data (not loading)
  else if (!isLoading && totalRecords === 0) {
    // If there are no records and not loading, show an empty table
    return (
      <div className="sku-list-table-container">
        <MotifTable
          className="LoadingTable"
          key={overlayNoRowsTemplate}
          // onGridReady={onGridReady}
          overlayNoRowsTemplate={overlayNoRowsTemplate}
          columnTypes={{}}
          //@ts-expect-error
          columnDefs={columnDefs}
          components={cellRenderers}
          description={`Manage SKUs, update costs or descriptions, and create API keys to register external orders and variable charges.`}
          //@ts-ignore
          rowData={[]}
          title={"SKU Listing"}
          suppressMovableColumns
          titleComplement={
            <MotifFormField>
              <SearchInput
                id={`skuList-${searchSkuListID_EmptyTable}`}
                className="skuListSearch-emptyTable"
                aria-label={"Search"}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(e: any) => {
                  if (e.target.value === "") {
                    handleSearch("");
                  }
                }}
                clearSearch={() => {
                  setPaginationObj({
                    ...paginationObj,
                    searchText: "",
                    currentPage: 1,
                    pageSize: paginationPageSize,
                  });
                }}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onEnter={(e: any) => handleSearch(e.target.value)}
                placeholder={"Search"}
                value={paginationObj.searchText}
              />
            </MotifFormField>
          }
        />
      </div>
    );
  }

  // Show table with data
  else if (!isLoading && totalRecords > 0) {
    return (
      <div className="sku-list-table-container">
        <MotifTable
          // onGridReady={onGridReady}
          key={overlayNoRowsTemplate}
          overlayNoRowsTemplate={overlayNoRowsTemplate}
          columnTypes={{}}
          paginateChildRows={true}
          //@ts-ignore
          columnDefs={columnDefs}
          components={cellRenderers}
          description={`Manage SKUs, update costs or descriptions, and create API keys to register external orders and variable charges.`}
          //@ts-ignore
          rowData={isLoading ? [] : rowData}
          suppressMovableColumns
          title={"SKU Listing"}
          titleComplement={
            <MotifFormField>
              <SearchInput
                id={`skuList-${searchSkuListID}`}
                className="skuListSearch"
                aria-label={"Search"}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(e: any) => {
                  const isContainEmoji = containsEmoji(e.target.value);
                  if (isContainEmoji) {
                    setSearchValidationError(EMOJI_ERROR_MESSAGE);
                  } else {
                    setSearchValidationError("");
                  }
                  if (e.target.value === "") {
                    handleSearch("");
                  }
                }}
                clearSearch={() => {
                  setSearchValidationError("");
                  setPaginationObj({
                    ...paginationObj,
                    searchText: "",
                    currentPage: 1,
                    pageSize: paginationPageSize,
                  });
                }}
                onEnter={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  const isContainEmoji = containsEmoji(e.currentTarget.value);
                  if (!isContainEmoji) {
                    handleSearch(e.currentTarget.value);
                  }
                }}
                placeholder={"Search"}
                value={paginationObj.searchText}
              />
              <div>
                {searchValidationError && (
                  <MotifErrorMessage className="search-helper-text">
                    {searchValidationError}
                  </MotifErrorMessage>
                )}
              </div>
            </MotifFormField>
          }
        />

        {showUpdateSkus && (
          <UpdateSkuModal
            hasAnyProductError={hasAnyProductError}
            setShow={setShowUpdateSkus}
            show={showUpdateSkus}
            onSave={() => {
              setReloadOnNewCreation(true);
              setShowSuccessPage(true);
            }}
            selectedSku={selectedSku}
            setIsAnySKUretired={setIsAnySKUretired}
            setReloadOnNewCreation={setReloadOnNewCreation}
          />
        )}

        {showPreviewSkus && (
          <PreviewSkuModal
            setShow={setShowPreviewSkus}
            show={showPreviewSkus}
            selectedSku={selectedSku}
            isConsumptionManager={isConsumptionManager}
          />
        )}
        {showDeleteSkuModal && (
          <DeleteSkuModal
            setShow={setShowDeleteSkuModal}
            show={showDeleteSkuModal}
            onDelete={(status: string) => {
              setReloadOnNewCreation(true);
              if (status === "success") {
                setShowDeleteSkuSuccess(true);
              } else if (status === "error") {
                setShowDeleteSkuError(true);
              }
            }}
            selectedSku={selectedSku}
          />
        )}

        {showDeleteSkuSuccess && (
          <SKUToast
            message="SKU has been deleted successfully"
            variantType="success"
          />
        )}

        {showDeleteSkuError && (
          <SKUToast message="Failed to delete SKU" variantType="error" />
        )}

        {showSuccessPage && (
          <SKUToast message="Sku has been updated successfully" />
        )}

        {showGenerateKeyModal && (
          <GenerateKeyModal
            hasApiKey={showViewKeysModal}
            show={showGenerateKeyModal}
            setShow={handleCloseGenerateKeyModal}
            selectedSku={selectedSku}
            showToast={showGenerateKeySuccessToast}
            setShowToast={setShowGenerateKeySuccessToast}
            showFull={showFullKey}
            setShowFull={setShowFullKey}
            keyVal={key}
            selectedSkuName={selectedSkuName}
          />
        )}

        <CustomPagination
          currentpage={paginationObj.currentPage}
          pageSize={paginationObj.pageSize}
          disableNext={false}
          onChangePageSize={(page: number) => {
            setPaginationObj({
              ...paginationObj,
              pageSize: Number(page),
              currentPage: 1,
            });
          }}
          onChangeNextPage={() => {
            setPaginationObj({
              ...paginationObj,
              currentPage: paginationObj.currentPage + 1,
            });
          }}
          onChangeFirstPage={() => {
            setPaginationObj({
              ...paginationObj,
              currentPage: 1,
            });
          }}
          onChangePreviousPage={() => {
            setPaginationObj({
              ...paginationObj,
              currentPage: paginationObj.currentPage - 1,
            });
          }}
          onChangeLastPage={() => {
            setPaginationObj({
              ...paginationObj,
              currentPage: totalPages,
            });
          }}
          pageSizeSelector={paginationPageSizeSelector}
          totalRecords={totalRecords}
        />
      </div>
    );
  }
}
